import axios, { CancelTokenSource } from 'axios'

import { ProjectSubcontractor } from './projects'

const BASE_URL = process.env.REACT_APP_NEXTERA_API

export const QUALITY_CATEGORY_NAME = 'Quality'
export const QUALITY_TYPE_EXTERNAL_CODE = 'QUALITY'

export type ObservationStatus = 'open' | 'closed'

export const listObservationTypes = () => {
  return axios.get(`${BASE_URL}/observation-types`).then(r => r.data)
}

export const listObservationTypesWithLabelTypes = () => {
  return axios
    .get(`${BASE_URL}/observation-types`, {
      params: {
        has_label_type: true,
        exclude_category: QUALITY_CATEGORY_NAME,
      },
    })
    .then(r => r.data)
}

/** Resolve the seeded Quality observation type (external_code=QUALITY). */
export const getQualityObservationType =
  (): Promise<ObservationType | null> => {
    return axios
      .get(`${BASE_URL}/observation-types`, {
        params: { external_code: QUALITY_TYPE_EXTERNAL_CODE },
      })
      .then(r => r.data)
      .then((body: { data?: ObservationType[] } | ObservationType[]) => {
        const rows = Array.isArray(body) ? body : (body.data ?? [])
        return rows[0] ?? null
      })
  }

export const listObservations = (
  projectId: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper filters type
  filters: any = {},
  cancelTokenSource?: CancelTokenSource,
) => {
  return axios
    .get(`${BASE_URL}/project/${projectId}/observations`, {
      params: filters,
      cancelToken: cancelTokenSource?.token,
    })
    .then(r => r.data)
}

/** Lean row for safety report generator checklist (matches GET …/observations/report_scope). */
export interface ObservationReportScope {
  id: number
  type: { id: number; name: string }
  project_floor: { id: number; floor_code: string }
  severity: string
  unsafe?: boolean
  registered_on: string
  /** Presigned URL for small UI thumbnails (processed or user small image); optional. */
  preview_image_url?: string | null
}

type ListObservationsMetaResponse<T> = {
  data?: T[]
  meta?: { next?: string | null; count?: number; next_offset?: number }
}

export const listObservationsReportScope = (
  projectId: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- same filters as listObservations
  filters: any = {},
  cancelTokenSource?: CancelTokenSource,
): Promise<ListObservationsMetaResponse<ObservationReportScope>> => {
  return axios
    .get(`${BASE_URL}/project/${projectId}/observations/report_scope`, {
      params: filters,
      cancelToken: cancelTokenSource?.token,
    })
    .then(r => r.data)
}

/** Paginate report_scope until no more rows or maxTotal reached. */
export const listAllObservationsReportScope = async (
  projectId: string,
  filters: Record<string, unknown>,
  maxTotal = 500,
): Promise<ObservationReportScope[]> => {
  const out: ObservationReportScope[] = []
  let offset = 0
  const limit = Math.min(500, maxTotal)
  while (out.length < maxTotal) {
    // eslint-disable-next-line no-await-in-loop -- sequential pagination
    const res = await listObservationsReportScope(projectId, {
      ...filters,
      limit,
      offset,
    })
    const batch = res.data ?? []
    out.push(...batch)
    if (batch.length < limit) break
    offset += limit
  }
  return out.slice(0, maxTotal)
}

export const retrieveObservation = (
  projectId: string,
  observationId: number,
) => {
  return axios
    .get(`${BASE_URL}/project/${projectId}/observation/${observationId}`)
    .then(r => r.data)
}

/** Minimal observation shape for the safety gallery floor plan map (no image URLs). */
export interface ObservationForMap {
  id: number
  type: { id: number; name: string }
  project_floor: { id: number; floor_code: string }
  labels: Array<{ id: number; floor_plan_position?: string | number[] }>
  viewpoint_point: { x: number; y: number } | null
  severity: string
  unsafe?: boolean
}

export const listObservationsForFloorPlan = (
  projectId: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- same filters as listObservations
  filters: any = {},
  cancelTokenSource?: CancelTokenSource,
) => {
  return axios
    .get(`${BASE_URL}/project/${projectId}/observations/for_floor_plan`, {
      params: filters,
      cancelToken: cancelTokenSource?.token,
    })
    .then(r => r.data)
}

export const updateObservation = (
  projectId: string,
  observationId: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper data type
  data: any,
) => {
  return axios
    .patch(
      `${BASE_URL}/project/${projectId}/observation/${observationId}`,
      data,
    )
    .then(r => r.data)
}

export const deleteObservation = (
  projectId: string,
  observationId: string | number,
) => {
  return axios.delete(
    `${BASE_URL}/project/${projectId}/observation/${observationId}`,
  )
}

/** Distinct quality observation titles for autocomplete (current project). */
export const listQualityObservationTitles = (
  projectId: string,
  q = '',
  cancelTokenSource?: CancelTokenSource,
): Promise<string[]> => {
  return axios
    .get(`${BASE_URL}/project/${projectId}/observations/title_autocomplete`, {
      params: q ? { q } : undefined,
      cancelToken: cancelTokenSource?.token,
    })
    .then(r => r.data)
    .then((body: { data?: string[] }) => body.data ?? [])
}

export const listObservationStats = (
  projectId: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper filters type
  filters?: any,
): Promise<ObservationStats> => {
  return axios
    .get(`${BASE_URL}/project/${projectId}/observations/stats`, {
      params: filters,
    })
    .then(r => r.data)
    .then(d => d.data)
}

export interface ObservationType {
  category: number | string
  name: string
  description: string
  id: number
  order: number
  display_in_filters: boolean
  default_selected: boolean
  default_severity?: string | null
  external_code?: string | null
}

interface ProcessedImage {
  base_image: number
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper yolo_results type
  yolo_results: any[]
  id: number
  registered_on: string
  last_modified_on: string
  ai_processed: boolean
  processed_image_key: string
  base_image_key: string
  processed_image_url: string
  base_image_url: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper viewpoints_image type
  viewpoints_image: any
  retrain: boolean
  reprocess: boolean
  verified: boolean
}

interface ObservationImage {
  user_image: string | undefined
  processed_image: ProcessedImage
}

export interface Observation {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper project_floor type
  project_floor: any
  id: number
  public_id: string
  project: number
  image: ObservationImage
  inspection?: number
  type: ObservationType
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper labels type
  labels: any[]
  verified: boolean
  is_manual: boolean
  subcontractor: ProjectSubcontractor
  severity: string
  unsafe: boolean
  notes: string
  title?: string | null
  status?: ObservationStatus
  registered_on: string
  claimed: boolean
  archived: boolean
  localImage?: string
}

const REPORT_DETAILS_MAX_IDS = 200

export const fetchObservationsReportDetails = (
  projectId: string,
  ids: number[],
): Promise<Observation[]> => {
  if (!ids.length) return Promise.resolve([])
  return axios
    .get(`${BASE_URL}/project/${projectId}/observations/report_details`, {
      params: { ids: ids.join(',') },
    })
    .then(r => r.data)
    .then((body: { data: Observation[] }) => body.data ?? [])
}

const REPORT_DETAILS_CHUNK_CONCURRENCY = 4

/** Chunked report_details for large unsafe selections (limited parallel fetch). */
export const fetchObservationsReportDetailsChunked = async (
  projectId: string,
  ids: number[],
): Promise<Observation[]> => {
  if (!ids.length) return []
  const slices: number[][] = []
  for (let i = 0; i < ids.length; i += REPORT_DETAILS_MAX_IDS) {
    slices.push(ids.slice(i, i + REPORT_DETAILS_MAX_IDS))
  }
  const out: Observation[][] = []
  for (let i = 0; i < slices.length; i += REPORT_DETAILS_CHUNK_CONCURRENCY) {
    const batch = slices.slice(i, i + REPORT_DETAILS_CHUNK_CONCURRENCY)
    // eslint-disable-next-line no-await-in-loop -- bounded batches
    const rows = await Promise.all(
      batch.map(slice => fetchObservationsReportDetails(projectId, slice)),
    )
    out.push(...rows)
  }
  return out.flat()
}

interface ObservationStatsSeveritySummary {
  low: number
  medium: number
  high: number
  life_threat: number
}

interface ObservationStatsSummary {
  total: number
  claimed: number
  unclaimed: number
  safe: number
  unsafe: number
  archived: number
  severity: ObservationStatsSeveritySummary
}

export interface ObservationStats extends ObservationStatsSummary {
  date_summary: Record<string, ObservationStatsSummary>
}
